import { SkipLimitPageSpec } from '@warebee/shared/athena-db-model';
import { KeysMatching } from '@warebee/shared/common';
import {
  SortDirection,
  StringSearchFilter,
  StringSearchFilterType,
} from '@warebee/shared/data-access-api-dto';
import { InferResult, sql } from 'kysely';
import _ from 'lodash';
import {
  orderJobsQueryBuilder,
  OrderJobsQueryBuilderParams,
} from './orderJobsQueryBuilder';

export type OrderLinesWithJobsDataRows = InferResult<
  ReturnType<typeof getOrderLinesWithJobsDataQuery>
>;
export type OrderLinesWithJobsDataRow = OrderLinesWithJobsDataRows[number];
export type OrderLinesWithJobsDataColumn = keyof OrderLinesWithJobsDataRow;

export type OrderLinesWithJobsFieldWithStringFilter = KeysMatching<
  OrderLinesWithJobsDataRow,
  string
>;

export type OrderLinesWithJobsFilterBy = Partial<
  Record<OrderLinesWithJobsFieldWithStringFilter, StringSearchFilter>
>;

export type OrderLinesWithJobsSortBy = {
  direction?: SortDirection;
  field: OrderLinesWithJobsDataColumn;
};

export type OrderLinesWithJobsPagedClientParams = {
  filterBy?: OrderLinesWithJobsFilterBy;
  page?: SkipLimitPageSpec;
  sortBy?: OrderLinesWithJobsSortBy[];
};

export type OrderLinesWithJobsPagedParams = OrderJobsQueryBuilderParams &
  OrderLinesWithJobsPagedClientParams;

export function getOrderLinesWithJobsDataQuery(
  params: OrderLinesWithJobsPagedParams,
) {
  let query = orderJobsQueryBuilder(params)
    .with(
      cte => cte('jobs_aggregated'),
      db => {
        return db
          .selectFrom('order_line_job')
          .select(['orderKey', 'orderLine'])
          .select(({ fn, cast }) => [
            fn
              .agg<string>('array_agg', [cast(sql.ref('picklistId'), 'json')])
              .as('jobsIds'),
          ])
          .groupBy(['orderKey', 'orderLine']);
      },
    )
    .with(
      cte => cte('order_lines_with_jobs'),
      db => {
        return db
          .selectFrom('orders')
          .leftJoin('jobs_aggregated', join =>
            join.on(eb =>
              eb.and([
                eb('orders.orderKey', '=', eb.ref('jobs_aggregated.orderKey')),
                eb(
                  'orders.orderLine',
                  '=',
                  eb.ref('jobs_aggregated.orderLine'),
                ),
              ]),
            ),
          )
          .selectAll();
      },
    )
    .selectFrom('order_lines_with_jobs')
    .selectAll()
    .$if(!_.isNil(params.page), qb =>
      qb.offset(params.page?.skip ?? 0).limit(params.page?.limit ?? 100),
    );

  if (params.filterBy) {
    query = _.reduce(
      params.filterBy,
      (q, value, key) => {
        if (
          value?.type === StringSearchFilterType.CONTAINS &&
          value?.value === 'undefined'
        ) {
          return q.where(key as any, 'is', null);
        }

        if (
          value?.type === StringSearchFilterType.CONTAINS &&
          !_.isEmpty(value?.value)
        ) {
          return q.where(key as any, 'like', `%${value.value}%`);
        }

        return q;
      },
      query,
    );
  }

  _.forEach(params.sortBy, sb => {
    query = query.orderBy(
      sb.field as any,
      sb.direction === SortDirection.ASC ? 'asc' : 'desc',
    );
  });

  return query;
}

export function getOrderLinesWithJobsTotalQuery(
  params: OrderJobsQueryBuilderParams,
) {
  let query = getOrderLinesWithJobsDataQuery(params)
    .clearSelect()
    .clearLimit()
    .clearOffset()
    .clearOrderBy()
    .select(({ fn }) => [fn.countAll<number>().as('totalCount')]);

  return query;
}
